// page-navigator.js — page indicator dots + scroll-snap sync.
//
// Given a scroll container that has N horizontally-arranged child pages
// (via CSS scroll-snap), this module:
//
//   1. Creates a set of indicator "dots" that reflect the current page.
//   2. Listens to scroll events to keep dots in sync with finger swipes.
//   3. Provides scrollToPage(index) for tap-to-jump navigation.
//   4. Emits page-change events so other UI (headings, etc.) can react.
//
// The implementation uses plain scroll offset math rather than
// IntersectionObserver so it behaves consistently in jsdom tests.

/**
 * @typedef {Object} PageNavigatorHandles
 * @property {HTMLElement} dotsElement
 * @property {() => number} getCurrentPage
 * @property {(index: number, behavior?: ScrollBehavior) => void} scrollToPage
 * @property {(fn: (page: number) => void) => () => void} onPageChange
 * @property {() => void} destroy
 */

/**
 * @param {HTMLElement} container - The horizontal scroll container.
 * @param {number} pageCount
 * @returns {PageNavigatorHandles}
 */
export function createPageNavigator(container, pageCount) {
  if (!(container instanceof HTMLElement)) {
    throw new TypeError('createPageNavigator: container must be an element');
  }
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    throw new RangeError('createPageNavigator: pageCount must be a positive integer');
  }

  const dotsElement = document.createElement('nav');
  dotsElement.className = 'page-nav';
  dotsElement.setAttribute('role', 'tablist');
  dotsElement.setAttribute('aria-label', 'Pages');

  /** @type {HTMLButtonElement[]} */
  const dots = [];
  for (let i = 0; i < pageCount; i++) {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'page-nav__dot';
    dot.setAttribute('role', 'tab');
    dot.setAttribute('aria-label', `Page ${i + 1}`);
    dot.addEventListener('click', () => {
      scrollToPage(i, 'smooth');
    });
    dots.push(dot);
    dotsElement.appendChild(dot);
  }

  let currentPage = 0;
  /** @type {Set<(page: number) => void>} */
  const listeners = new Set();

  function updateDots() {
    for (let i = 0; i < dots.length; i++) {
      const active = i === currentPage;
      dots[i].classList.toggle('page-nav__dot--active', active);
      dots[i].setAttribute('aria-selected', active ? 'true' : 'false');
      dots[i].tabIndex = active ? 0 : -1;
    }
  }

  function setCurrentPage(index) {
    const clamped = Math.max(0, Math.min(pageCount - 1, index));
    if (clamped === currentPage) return;
    currentPage = clamped;
    updateDots();
    for (const fn of listeners) {
      try {
        fn(currentPage);
      } catch (err) {
        console.error('[page-navigator] listener threw:', err);
      }
    }
  }

  function computePageFromScroll() {
    const width = container.clientWidth;
    if (width === 0) return 0;
    return Math.round(container.scrollLeft / width);
  }

  function handleScroll() {
    setCurrentPage(computePageFromScroll());
  }

  container.addEventListener('scroll', handleScroll, { passive: true });

  function scrollToPage(index, behavior = 'auto') {
    const clamped = Math.max(0, Math.min(pageCount - 1, index));
    const width = container.clientWidth;
    container.scrollTo({ left: clamped * width, behavior });
    // jsdom doesn't emit scroll events from scrollTo(), so update
    // immediately. In real browsers this is a no-op because
    // handleScroll will run on the next frame.
    setCurrentPage(clamped);
  }

  function getCurrentPage() {
    return currentPage;
  }

  function onPageChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  function destroy() {
    container.removeEventListener('scroll', handleScroll);
    listeners.clear();
    dotsElement.remove();
  }

  // Initial state.
  updateDots();

  return { dotsElement, getCurrentPage, scrollToPage, onPageChange, destroy };
}
